import dayjs from 'dayjs';
import {
  DateTimeSetExpr,
  dayOfWeekFromJSON,
  dayOfWeekToJSON,
  DayOfWeek,
  RecurringWindowExpr,
  TimeOfDayRange,
  TimeRangeOp,
  VariableValue,
} from '@/proto/logiblocks/logiblocks';
import {
  DayOfWeekName,
  SavedDateList,
  SavedDateListInput,
  SavedDateListTimeRange,
  SystemDatePresetSummary,
} from '@/apis/logiblocksAPI';
import { getUserTimeZone } from '@/common/utils/utils';
import {
  getDayOfWeekShortLabel,
  secondsToDayjs,
  utcSecondsToUserSeconds,
} from '@/pages/Logiblocks/dateTimeUtils';
import {
  ALL_DAYS,
  ALL_DAY_WINDOW,
  DateSetEntries,
  DateSetValue,
  DateWindowEntry,
  DayWindowEntry,
  EntryTimeWindow,
} from './dateSetTypes';

// ---------------------------------------------------------------------------
// Entries
// ---------------------------------------------------------------------------

/** A fresh all-day entry for a newly toggled-on date. */
export function newDateEntry(isoDate: string): DateWindowEntry {
  return { date: isoDate, window: ALL_DAY_WINDOW };
}

/** A fresh all-day entry for a newly toggled-on day of the week. */
export function newDayEntry(day: DayOfWeek): DayWindowEntry {
  return { day, window: ALL_DAY_WINDOW };
}

/** How EntryChipList keys, orders and labels the date entries. */
export const dateChipConfig = {
  getKey: (entry: DateWindowEntry) => entry.date,
  compare: (a: DateWindowEntry, b: DateWindowEntry) =>
    a.date.localeCompare(b.date),
  getLabel: (entry: DateWindowEntry) => formatChipDateLabel(entry.date),
};

/** How EntryChipList keys, orders and labels the day-of-week entries. */
export const dayChipConfig = {
  getKey: (entry: DayWindowEntry) => entry.day,
  compare: (a: DayWindowEntry, b: DayWindowEntry) => a.day - b.day,
  getLabel: (entry: DayWindowEntry) => getDayOfWeekShortLabel(entry.day),
};

/** "Custom dates" or "Custom days" — the name of an ad-hoc (unsaved) selection. */
export function customSetName(kind: DateSetEntries['kind']): string {
  return kind === 'dates' ? 'Custom dates' : 'Custom days';
}

function entriesWindows(entries: DateSetEntries): EntryTimeWindow[] {
  return entries.entries.map((e) => e.window);
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** Returns `seconds` since midnight as "7 AM", or as "9:30 AM" when it isn't on the hour. */
function formatWindowBound(seconds: number): string {
  const format = seconds % 3600 === 0 ? 'h A' : 'h:mm A';
  return secondsToDayjs(seconds).format(format);
}

/** "All day" or "6 AM - 7:30 PM" for one entry's time window. */
export function formatEntryTimeLabel(window: EntryTimeWindow): string {
  if (window.allDay) return 'All day';
  const start = formatWindowBound(window.startSeconds ?? 0);
  const end = formatWindowBound(window.endSeconds ?? 0);
  return `${start} - ${end}`;
}

/** "Dec 11", or "Dec 11, 2027" when the date falls outside the current
 * calendar year. */
export function formatChipDateLabel(isoDate: string): string {
  const d = dayjs(isoDate);
  const isCurrentYear = d.year() === dayjs().year();
  return d.format(isCurrentYear ? 'MMM D' : 'MMM D, YYYY');
}

function windowsEqual(a: EntryTimeWindow, b: EntryTimeWindow): boolean {
  if (a.allDay !== b.allDay) return false;
  if (a.allDay) return true;
  return a.startSeconds === b.startSeconds && a.endSeconds === b.endSeconds;
}

/** "All day", the range every window shares, or "Mixed" — the descriptor
 * after a value-menu row's count and in the trigger's own label. */
export function computeDescriptor(windows: EntryTimeWindow[]): string {
  if (windows.length === 0) return 'All day';
  const [first, ...rest] = windows;
  const allSame = rest.every((w) => windowsEqual(w, first));
  if (!allSame) return 'Mixed';
  return formatEntryTimeLabel(first);
}

/** "{Name} ({count}) {descriptor}" — the value-menu row format, also used for
 * the trigger's collapsed label. The descriptor is omitted when empty. */
export function formatDateSetValueLabel(
  name: string,
  count: number,
  descriptor = ''
): string {
  const label = `${name} (${count})`;
  return descriptor ? `${label} ${descriptor}` : label;
}

/** The time window of a system preset's rows. */
export function presetWindow(preset: SystemDatePresetSummary): EntryTimeWindow {
  return {
    allDay: preset.all_day,
    startSeconds: preset.start_seconds ?? undefined,
    endSeconds: preset.end_seconds ?? undefined,
  };
}

/** The time window of each entry of a saved list. */
export function savedListWindows(list: SavedDateList): EntryTimeWindow[] {
  return list.entries.map((e) => timeRangeToWindow(e.time_range));
}

/** "All day" / a shared range / "Mixed" for a system preset. */
export function presetDescriptor(preset: SystemDatePresetSummary): string {
  return computeDescriptor([presetWindow(preset)]);
}

/** "All day" / a shared range / "Mixed" for a saved list. */
export function savedListDescriptor(list: SavedDateList): string {
  return computeDescriptor(savedListWindows(list));
}

function formatEntriesLabel(entries: DateSetEntries): string {
  return formatDateSetValueLabel(
    customSetName(entries.kind),
    entries.entries.length,
    computeDescriptor(entriesWindows(entries))
  );
}

/** The canvas-node subtitle fragment for a DateTimeSetExpr leaf (everything
 * after "{lhs} {op}"), most-specific first: preset/saved-list denormalized
 * name, ad-hoc multi-date count, or day-of-week collapsed to a single range
 * when every day shares one, else a count. */
export function formatDateTimeSetExprSubtitle(expr: DateTimeSetExpr): string {
  if (expr.system_preset_id) {
    return expr.display_label || expr.system_preset_id;
  }
  if (expr.saved_list_id) {
    const count =
      expr.specific_dates?.dates.length ?? expr.days_of_week?.days.length;
    return expr.display_label || (count ? `${count} dates` : 'Saved list');
  }
  if (expr.specific_dates) {
    return `${expr.specific_dates.dates.length} dates`;
  }
  if (expr.days_of_week) {
    const days = expr.days_of_week.days;
    const windows = days.map((d) => timeRangeToWindow(d.time_range));
    const [first, ...rest] = windows;
    const allSame = first && rest.every((w) => windowsEqual(w, first));
    if (first && allSame) {
      return formatEntryTimeLabel(first);
    }
    return `${days.length} days`;
  }
  return '';
}

// ---------------------------------------------------------------------------
// Proto <-> editing-model conversion
// ---------------------------------------------------------------------------

/** Reads a stored time range (proto or saved-list payload) as an editing
 * window; a missing range means all day. */
export function timeRangeToWindow(
  tr: TimeOfDayRange | SavedDateListTimeRange | undefined
): EntryTimeWindow {
  if (!tr || tr.all_day) return ALL_DAY_WINDOW;
  return {
    allDay: false,
    startSeconds: tr.start_seconds ?? 0,
    endSeconds: tr.end_seconds ?? 0,
  };
}

function windowToTimeRange(window: EntryTimeWindow): TimeOfDayRange {
  if (window.allDay) return { all_day: true };
  return {
    all_day: false,
    start_seconds: window.startSeconds ?? 0,
    end_seconds: window.endSeconds ?? 0,
  };
}

/** The entries portion of a DateTimeSetExpr, from the editing model. */
function entriesToProto(
  entries: DateSetEntries
): Pick<DateTimeSetExpr, 'specific_dates' | 'days_of_week'> {
  if (entries.kind === 'dates') {
    return {
      specific_dates: {
        dates: entries.entries.map((e) => ({
          date: e.date,
          time_range: windowToTimeRange(e.window),
        })),
      },
    };
  }
  return {
    days_of_week: {
      days: entries.entries.map((e) => ({
        day: e.day,
        time_range: windowToTimeRange(e.window),
      })),
    },
  };
}

/** Build a full DateTimeSetExpr for an ad-hoc (unsaved) custom selection. */
export function buildCustomDateTimeSetExpr(
  variable: DateTimeSetExpr['variable'],
  value: DateSetValue,
  displayLabel: string
): DateTimeSetExpr {
  return {
    variable,
    iana_timezone: value.ianaTimezone,
    operator: value.operator,
    display_label: displayLabel,
    ...entriesToProto(value.entries),
  };
}

/** Whether a DateTimeSetExpr carries its own inline dates/days (as opposed to
 * referencing a preset or saved list, or being empty). */
export function hasInlineEntries(expr: DateTimeSetExpr): boolean {
  return (
    (expr.specific_dates?.dates.length ?? 0) > 0 ||
    (expr.days_of_week?.days.length ?? 0) > 0
  );
}

/** Read a DateSetValue back out of a stored DateTimeSetExpr, for the editor
 * and the display formatters. Only meaningful for the inline
 * specific_dates/days_of_week sources -- a preset/saved-list-sourced expr's
 * entries are resolved server-side and aren't reconstructed here. */
export function dateSetValueFromExpr(expr: DateTimeSetExpr): DateSetValue {
  const entries: DateSetEntries = expr.days_of_week
    ? {
        kind: 'daysOfWeek',
        entries: expr.days_of_week.days.map((d) => ({
          day: d.day,
          window: timeRangeToWindow(d.time_range),
        })),
      }
    : {
        kind: 'dates',
        entries: (expr.specific_dates?.dates ?? []).map((d) => ({
          date: d.date,
          window: timeRangeToWindow(d.time_range),
        })),
      };
  return { entries, ianaTimezone: expr.iana_timezone, operator: expr.operator };
}

// ---------------------------------------------------------------------------
// SavedDateList API payload <-> editing-model conversion
// ---------------------------------------------------------------------------

/** Build the request body for creating/updating a saved list from the
 * editing model. */
export function dateSetValueToSavedListInput(
  name: string,
  value: DateSetValue
): SavedDateListInput {
  if (value.entries.kind === 'dates') {
    return {
      name,
      kind: 'specific_dates',
      iana_timezone: value.ianaTimezone,
      entries: value.entries.entries.map((e) => ({
        date: e.date,
        time_range: windowToTimeRange(e.window),
      })),
    };
  }
  return {
    name,
    kind: 'days_of_week',
    iana_timezone: value.ianaTimezone,
    entries: value.entries.entries.map((e) => ({
      // dayOfWeekToJSON returns the enum's own name, which is a DayOfWeekName
      // for every day an entry can hold.
      day: dayOfWeekToJSON(e.day) as DayOfWeekName,
      time_range: windowToTimeRange(e.window),
    })),
  };
}

/** Read a saved list's API payload back into the editing model, for the
 * list-edit panel. */
export function savedListToDateSetValue(list: SavedDateList): DateSetValue {
  const entries: DateSetEntries =
    list.kind === 'specific_dates'
      ? {
          kind: 'dates',
          entries: list.entries.map((e) => ({
            date: e.date,
            window: timeRangeToWindow(e.time_range),
          })),
        }
      : {
          kind: 'daysOfWeek',
          entries: list.entries.map((e) => ({
            day: dayOfWeekFromJSON(e.day),
            window: timeRangeToWindow(e.time_range),
          })),
        };
  return {
    entries,
    ianaTimezone: list.iana_timezone,
    operator: TimeRangeOp.WITHIN,
  };
}

// ---------------------------------------------------------------------------
// Legacy-data compatibility
// ---------------------------------------------------------------------------

/** Fold a legacy RecurringWindowExpr (one shared start/end across every
 * selected day, seconds stored in UTC) into the per-entry editing model, so
 * the picker can display and re-edit an already-saved condition. Each
 * `DayOfWeek` becomes its own entry sharing the converted range; an empty
 * day list means every day. */
export function materializeLegacyRecurringWindow(
  expr: RecurringWindowExpr,
  displayIana: string,
  toUserSeconds: (utcSeconds: number, tz: string) => number
): DateSetValue {
  const hasTime = expr.start_seconds != null || expr.end_seconds != null;
  const window: EntryTimeWindow = hasTime
    ? {
        allDay: false,
        startSeconds: toUserSeconds(expr.start_seconds ?? 0, displayIana),
        endSeconds: toUserSeconds(expr.end_seconds ?? 0, displayIana),
      }
    : ALL_DAY_WINDOW;

  const days = expr.days_of_week.length ? expr.days_of_week : ALL_DAYS;

  return {
    entries: {
      kind: 'daysOfWeek',
      entries: days.map((day) => ({ day, window })),
    },
    ianaTimezone: displayIana,
    operator: expr.operator,
  };
}

// ---------------------------------------------------------------------------
// The condition field's derived values
// ---------------------------------------------------------------------------

/** What a condition's value field is currently showing, as stored on the leaf. */
export interface DateSetFieldSource {
  expr: DateTimeSetExpr;
  /** The legacy day-of-week shape, when the condition hasn't been re-edited. */
  legacyRecurringWindowExpr?: RecurringWindowExpr;
  /** The oldest single-literal shape. */
  legacyLiteralValue?: VariableValue;
}

/** The trigger's collapsed label — "{Name} ({count}) {descriptor}" — for
 * whichever shape the condition carries, or null when nothing is selected.
 * A preset/saved list that hasn't loaded yet falls back to its denormalized
 * display_label. */
export function getTriggerLabel(
  source: DateSetFieldSource,
  presets: SystemDatePresetSummary[] | undefined,
  savedLists: SavedDateList[] | undefined
): string | null {
  const { expr, legacyRecurringWindowExpr, legacyLiteralValue } = source;

  if (expr.system_preset_id) {
    const preset = presets?.find((p) => p.preset_id === expr.system_preset_id);
    if (!preset) return expr.display_label || null;
    return formatDateSetValueLabel(
      preset.display_name,
      preset.count,
      presetDescriptor(preset)
    );
  }
  if (expr.saved_list_id) {
    const list = savedLists?.find((l) => l.id === expr.saved_list_id);
    if (!list) return expr.display_label || null;
    return formatDateSetValueLabel(
      list.name,
      list.entry_count,
      savedListDescriptor(list)
    );
  }
  if (hasInlineEntries(expr)) {
    return formatEntriesLabel(dateSetValueFromExpr(expr).entries);
  }
  if (legacyRecurringWindowExpr) {
    const hasLegacyData =
      legacyRecurringWindowExpr.days_of_week.length > 0 ||
      legacyRecurringWindowExpr.start_seconds != null ||
      legacyRecurringWindowExpr.end_seconds != null;
    if (!hasLegacyData) return null;
    return formatEntriesLabel(
      materializeLegacyRecurringWindow(
        legacyRecurringWindowExpr,
        getUserTimeZone().name,
        utcSecondsToUserSeconds
      ).entries
    );
  }
  if (legacyLiteralValue?.datetime_value) {
    return formatDateSetValueLabel(customSetName('dates'), 1);
  }
  return null;
}

/** The value the "Custom dates" calendar opens with: the condition's own
 * inline entries, else its materialized legacy day-of-week window, else an
 * empty date set. */
export function buildInitialCustomValue(
  source: DateSetFieldSource
): DateSetValue {
  const { expr, legacyRecurringWindowExpr } = source;
  if (hasInlineEntries(expr)) return dateSetValueFromExpr(expr);
  if (legacyRecurringWindowExpr) {
    return materializeLegacyRecurringWindow(
      legacyRecurringWindowExpr,
      getUserTimeZone().name,
      utcSecondsToUserSeconds
    );
  }
  return {
    entries: { kind: 'dates', entries: [] },
    ianaTimezone: getUserTimeZone().name,
    operator: expr.operator,
  };
}
